import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const GST_RATES = [0, 5, 12, 18, 28];
const SCHEDULES = ['none','OTC','H','H1','X','G','J'];
const UNITS = ['TAB','CAP','SYP','INJ','OINT','CREAM','DROP','GEL','SACHET','STRIP','BOX','BOTTLE','VIAL','AMPOULE','KG','GM','LTR','ML','PIECE','PACK','TUBE','POUCH'];

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [form, setForm] = useState({
    name: '', generic_name: '', sku: '', barcode: '', hsn_code: '', drug_schedule: 'none',
    unit_of_measure: 'STRIP', pack_size: '', mrp: '', purchase_price: '', selling_price: '',
    gst_rate: '12', cess_rate: '0', discount_pct: '0', reorder_level: '10', min_order_qty: '1',
    max_stock_level: '1000', storage_conditions: '', is_narcotic: false, is_refrigerated: false,
    requires_prescription: false, category_id: '', manufacturer_id: '', notes: ''
  });

  useEffect(() => {
    productsAPI.categories().then(r => setCategories(r.data)).catch(() => {});
    productsAPI.manufacturers().then(r => setManufacturers(r.data)).catch(() => {});
    if (isEdit) {
      productsAPI.get(id).then(r => {
        const p = r.data;
        setForm({
          name: p.name || '', generic_name: p.generic_name || '', sku: p.sku || '',
          barcode: p.barcode || '', hsn_code: p.hsn_code || '', drug_schedule: p.drug_schedule || 'none',
          unit_of_measure: p.unit_of_measure || 'STRIP', pack_size: p.pack_size || '',
          mrp: p.mrp || '', purchase_price: p.purchase_price || '', selling_price: p.selling_price || '',
          gst_rate: p.gst_rate || '12', cess_rate: p.cess_rate || '0', discount_pct: p.discount_pct || '0',
          reorder_level: p.reorder_level || '10', min_order_qty: p.min_order_qty || '1',
          max_stock_level: p.max_stock_level || '1000', storage_conditions: p.storage_conditions || '',
          is_narcotic: p.is_narcotic || false, is_refrigerated: p.is_refrigerated || false,
          requires_prescription: p.requires_prescription || false,
          category_id: p.category_id || '', manufacturer_id: p.manufacturer_id || '', notes: p.notes || ''
        });
      }).catch(() => toast.error('Product not found'));
    }
  }, [id, isEdit]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target?.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.hsn_code) { toast.error('Product name and HSN code are required'); return; }
    setLoading(true);
    try {
      if (isEdit) { await productsAPI.update(id, form); toast.success('Product updated'); }
      else { await productsAPI.create(form); toast.success('Product created'); }
      navigate('/products');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save product'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div><h1>{isEdit ? 'Edit Product' : 'Add New Product'}</h1></div>
        <button onClick={() => navigate('/products')} className="btn btn-secondary">Cancel</button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-header"><h2>Basic Information</h2></div>
          <div className="card-body">
            <div className="form-row form-row-3">
              <div className="form-group"><label className="form-label">Product Name *</label><input className="form-input" value={form.name} onChange={set('name')} placeholder="e.g., Paracetamol 500mg" required /></div>
              <div className="form-group"><label className="form-label">Generic Name</label><input className="form-input" value={form.generic_name} onChange={set('generic_name')} placeholder="e.g., Paracetamol" /></div>
              <div className="form-group"><label className="form-label">SKU</label><input className="form-input" value={form.sku} onChange={set('sku')} placeholder="Auto-generated if empty" /></div>
            </div>
            <div className="form-row form-row-4">
              <div className="form-group"><label className="form-label">Barcode</label><input className="form-input" value={form.barcode} onChange={set('barcode')} /></div>
              <div className="form-group"><label className="form-label">HSN Code *</label><input className="form-input" value={form.hsn_code} onChange={set('hsn_code')} placeholder="e.g., 30049099" required /></div>
              <div className="form-group"><label className="form-label">Drug Schedule</label><select className="form-select" value={form.drug_schedule} onChange={set('drug_schedule')}>{SCHEDULES.map(s => <option key={s} value={s}>{s === 'none' ? 'General / None' : `Schedule ${s}`}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Unit of Measure</label><select className="form-select" value={form.unit_of_measure} onChange={set('unit_of_measure')}>{UNITS.map(u => <option key={u} value={u}>{u}</option>)}</select></div>
            </div>
            <div className="form-row form-row-3">
              <div className="form-group"><label className="form-label">Pack Size</label><input className="form-input" value={form.pack_size} onChange={set('pack_size')} placeholder="e.g., 10x10, 100ml" /></div>
              <div className="form-group"><label className="form-label">Category</label><select className="form-select" value={form.category_id} onChange={set('category_id')}><option value="">Select Category</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Manufacturer</label><select className="form-select" value={form.manufacturer_id} onChange={set('manufacturer_id')}><option value="">Select Manufacturer</option>{manufacturers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header"><h2>Pricing & Tax</h2></div>
          <div className="card-body">
            <div className="form-row form-row-4">
              <div className="form-group"><label className="form-label">MRP (₹)</label><input type="number" step="0.01" className="form-input" value={form.mrp} onChange={set('mrp')} /></div>
              <div className="form-group"><label className="form-label">Purchase Price (₹)</label><input type="number" step="0.01" className="form-input" value={form.purchase_price} onChange={set('purchase_price')} /></div>
              <div className="form-group"><label className="form-label">Selling Price (₹)</label><input type="number" step="0.01" className="form-input" value={form.selling_price} onChange={set('selling_price')} /></div>
              <div className="form-group"><label className="form-label">GST Rate (%)</label><select className="form-select" value={form.gst_rate} onChange={set('gst_rate')}>{GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}</select></div>
            </div>
            <div className="form-row form-row-4">
              <div className="form-group"><label className="form-label">Cess Rate (%)</label><input type="number" step="0.01" className="form-input" value={form.cess_rate} onChange={set('cess_rate')} /></div>
              <div className="form-group"><label className="form-label">Discount (%)</label><input type="number" step="0.01" className="form-input" value={form.discount_pct} onChange={set('discount_pct')} /></div>
              <div className="form-group"><label className="form-label">Reorder Level</label><input type="number" className="form-input" value={form.reorder_level} onChange={set('reorder_level')} /></div>
              <div className="form-group"><label className="form-label">Min Order Qty</label><input type="number" className="form-input" value={form.min_order_qty} onChange={set('min_order_qty')} /></div>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header"><h2>Additional Info</h2></div>
          <div className="card-body">
            <div className="form-row form-row-3">
              <div className="form-group"><label className="form-label">Storage Conditions</label><input className="form-input" value={form.storage_conditions} onChange={set('storage_conditions')} placeholder="e.g., Store below 25°C" /></div>
              <div className="form-group" style={{ display:'flex', flexDirection:'column', gap:8, paddingTop:24 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}><input type="checkbox" checked={form.requires_prescription} onChange={set('requires_prescription')} /> Requires Prescription</label>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}><input type="checkbox" checked={form.is_refrigerated} onChange={set('is_refrigerated')} /> Refrigerated</label>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}><input type="checkbox" checked={form.is_narcotic} onChange={set('is_narcotic')} /> Narcotic / Controlled</label>
              </div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" value={form.notes} onChange={set('notes')} rows={3} /></div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Update Product' : 'Create Product'}
          </button>
          <button type="button" className="btn btn-secondary btn-lg" onClick={() => navigate('/products')}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
